import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { useCfmContext } from "../../../Context/CfmContext";
import { hexToRgba } from "./GetGradientColor";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import { useRef } from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";

/**
 * Renders a stacked investment graph displaying withdrawals, adhoc withdrawals, adhoc contributions, and fund value
 * for multiple investments over a range of ages. Supports both print and responsive display modes.
 *
 * @function
 * @param {boolean} [isPrint=true] - If true, renders the chart with fixed width and height for printing; otherwise, uses responsive sizing.
 * @returns {JSX.Element} The rendered stacked investment graph component.
 *
 * @example
 * <StackedInvestmentGraph isPrint={false} />
 *
 * @remarks
 * - Uses context from `useCfmContext` to access investment results.
 * - Dynamically generates series and colors for each investment.
 * - Displays a placeholder chart if no investment data is available.
 * - Includes custom legends, tooltips, and axis formatting.
 * - Supports exporting the chart as an image via `apiRef`.
 */
const StackedInvestmentGraph = ({
  isPrint = false,
  animated = false,
  apiRef = null,
  idOverride,
}) => {
  const { state, dispatch } = useCfmContext();
  const chartOnlyRef = useRef();

  // All ages across ALL investments
  const ageSet = new Set();
  (state.investmentResults || []).forEach((inv) => {
    inv.investmentFunds?.forEach((row) => ageSet.add(row.age));
  });
  const allAges = Array.from(ageSet).sort((a, b) => a - b);

  const parse = (v) => parseFloat((v || "0").replace(/[^0-9.-]+/g, "")) || 0;

  const colors = [
    {
      line: "#355c8d",
      bar: "#22c55e",
      adhoc: "#9333ea",
      contribution: "#facc15",
    },
    {
      line: "#A1887F",
      bar: "#e5b368",
      adhoc: "#ff8a80",
      contribution: "#90caf9",
    },
    {
      line: "#ff8a65",
      bar: "#fdd835",
      adhoc: "#ce93d8",
      contribution: "#ffee58",
    },
    {
      line: "#ba68c8",
      bar: "#9575cd",
      adhoc: "#e1bee7",
      contribution: "#fff176",
    },
  ];

  // Build series for each investment
  const series = (state.investmentResults || []).flatMap((inv, idx) => {
    const label = `${inv.name}: ${inv.type}` || `Investment ${idx + 1}`;
    const colorSet = colors[idx % colors.length];
    const fundMap = new Map();

    inv.investmentFunds?.forEach((row) => {
      fundMap.set(row.age, {
        fund: Math.max(
          0,
          parseFloat(String(row.fundValue || "0").replace(/[^0-9.-]+/g, "")) ||
            0
        ),
        withdrawals: parseFloat(
          (row.regularWithdrawals || "0").replace(/[^0-9.-]+/g, "")
        ),
        adhocWithdrawals: parseFloat(
          (row.adhocWithdrawals || "0").replace(/[^0-9.-]+/g, "")
        ),
        adhocContribution: parseFloat(
          (row.adhocInvestment || "0").replace(/[^0-9.-]+/g, "")
        ),
      });
    });

    // This investment's stack & line
    return [
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.withdrawals || 0),
        label: `${label} - Withdrawals`,
        color: colorSet.bar,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocWithdrawals || 0),
        label: `${label} - Adhoc Withdrawals`,
        color: colorSet.adhoc,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocContribution || 0),
        label: `${label} - Adhoc Contributions`,
        color: colorSet.contribution,
      },
      {
        type: "line",
        area: true,
        data: allAges.map((age) => fundMap.get(age)?.fund || 0),
        label: `${label} - Fund Value`,
        color: colorSet.line,
      },
    ];
  });

  const placeholderSeries = [
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Withdrawals",
      color: colors[0].bar,
    },
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Adhoc Withdrawals",
      color: colors[0].adhoc,
    },
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Adhoc Contributions",
      color: colors[0].contribution,
    },
    {
      type: "line",
      data: [0],
      label: "Fund Value",
      color: colors[0].line,
    },
  ];

  const chartSeries = series.length ? series : placeholderSeries;
  const chartAges = allAges.length ? allAges : [];

  const visibleSeries = chartSeries.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );

  const graphHeight = useGraphHeight(isPrint);
  // console.log("Graph Height:", graphHeight, isPrint);

  return (
    // <Stack spacing={2}>
    //   <Button
    //     onClick={() => apiRef.current.exportAsImage()}
    //     variant="contained"
    //     sx={{ mb: 1 }}
    //   >
    //     Print
    //   </Button>
    <div
      ref={chartOnlyRef}
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "#ffffff",
      }}
      id={idOverride || "stacked-inv-graph"}
    >
      {/* <div className="cfm-graph-wrapper"> */}
      <ChartDataProviderPro
        key="multi-investment-graph"
        apiRef={apiRef}
        // width={1200}
        height={graphHeight}
        fillMode="none"
        series={visibleSeries}
        xAxis={[
          {
            id: "age-axis",
            data: chartAges,
            scaleType: "band",
            label: "Age",
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Fund Value",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            {/* Fund line area gradients */}
            <defs>
              {chartSeries
                .filter((s) => s.type === "line")
                .map((s, idx) => (
                  <linearGradient
                    key={`grad-${idx}`}
                    id={`grad-${idx}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={hexToRgba(s.color, 0.2)} />
                    <stop offset="100%" stopColor={hexToRgba(s.color, 0)} />
                  </linearGradient>
                ))}
            </defs>

            <BarPlot springconfig={{ tension: 80, friction: 80 }} />

            {chartSeries
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <AreaPlot
                  key={`area-${idx}`}
                  seriesid={s.label}
                  springconfig={{ tension: 80, friction: 80 }}
                  slots={{
                    area: ({ d, style }) => (
                      <path
                        d={d}
                        style={style}
                        fill={`url(#grad-${idx})`}
                        stroke="none"
                      />
                    ),
                  }}
                />
              ))}

            {chartSeries
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <LinePlot
                  key={`line-${idx}`}
                  seriesid={s.label}
                  slotProps={{
                    line: {
                      style: {
                        strokeWidth: 4,
                      },
                    },
                  }}
                  springconfig={{ tension: 80, friction: 80 }}
                />
              ))}

            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
      {/* </div> */}
    </div>
    // </Stack>
  );
};

export default StackedInvestmentGraph;
